import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Button from '../components/Button';
import { FadeIn } from '../components/FadeIn';

import fazendaImg from '../assets/fazenda-leiteira.webp';
import obrasImg from '../assets/controle-obras.png';
import locacaoImg from '../assets/locacao-equipamentos.png';

const products = [
  {
    id: 'fazenda-leiteira',
    title: 'Fazenda Leiteira',
    category: 'Gestão Rural',
    price: 'A partir de R$ 49/mês',
    image: fazendaImg,
    icon: 'fas fa-cow',
    color: '#22c55e',
    path: '/product/fazenda-leiteira',
    plansPath: '/planos/fazenda-leiteira',
    short:
      'Controle produção, rebanho, custos, estoque, reprodução, saúde e lucratividade.',
    description:
      'Sistema completo para transformar a rotina da fazenda leiteira em dados claros para tomada de decisão.',
    features: [
      'Produção diária de leite',
      'Cadastro e histórico dos animais',
      'Controle de saúde e reprodução',
      'Estoque e insumos',
      'Custos e lucro por período',
    ],
  },
  {
    id: 'controle-de-obras',
    title: 'Controle de Obras',
    category: 'Construção Civil',
    price: 'Planos sob medida',
    image: obrasImg,
    icon: 'fas fa-helmet-safety',
    color: '#f59e0b',
    path: '/product/controle-de-obras',
    plansPath: '/planos/controle-de-obras',
    short:
      'Gerencie obras, etapas, cronogramas, equipes, materiais, fotos e custos.',
    description:
      'Plataforma para acompanhar a execução da obra com visão clara de prazos, custos e evolução.',
    features: [
      'Cadastro de obras',
      'Etapas e cronograma',
      'Custos por obra',
      'Registro fotográfico',
      'Relatórios de acompanhamento',
    ],
  },
  {
    id: 'locafacil-obras',
    title: 'LocaFácil Obras',
    category: 'Locação de Equipamentos',
    price: 'Planos sob medida',
    image: locacaoImg,
    icon: 'fas fa-truck-ramp-box',
    color: '#06b6d4',
    path: '/product/locafacil-obras',
    plansPath: '/planos/locafacil-obras',
    short:
      'Controle equipamentos, contratos, reservas, manutenção, devoluções e faturamento.',
    description:
      'Sistema para organizar locadoras de equipamentos com visão operacional e financeira.',
    features: [
      'Cadastro de equipamentos',
      'Contratos e reservas',
      'Agenda de locações',
      'Manutenção preventiva',
      'Financeiro da locação',
    ],
  },
  {
    id: 'atende24h',
    title: 'Atende24h IA',
    category: 'Atendimento Inteligente',
    price: 'Planos sob medida',
    image: null,
    icon: 'fas fa-robot',
    color: '#6366f1',
    path: '/product/atende24h',
    plansPath: '/planos/atende24h',
    short:
      'Automatize WhatsApp, Instagram, site e atendimento interno com IA.',
    description:
      'Atendimento inteligente para responder rápido, organizar conversas e capturar oportunidades 24 horas por dia.',
    features: [
      'WhatsApp inteligente',
      'IA treinada com documentos',
      'Multiatendimento',
      'Histórico das conversas',
      'Automações personalizadas',
    ],
  },
];

export const Solucoes = () => {
  const navigate = useNavigate();

  const [selectedId, setSelectedId] = useState(products[0].id);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('Todas');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const categories = ['Todas', ...new Set(products.map((item) => item.category))];

  const filteredProducts = useMemo(() => {
    return products.filter((item) => {
      const matchesCategory = category === 'Todas' || item.category === category;
      const term = search.toLowerCase();

      const matchesSearch =
        item.title.toLowerCase().includes(term) ||
        item.short.toLowerCase().includes(term) ||
        item.category.toLowerCase().includes(term);

      return matchesCategory && matchesSearch;
    });
  }, [search, category]);

  const selectedProduct =
    products.find((item) => item.id === selectedId) || products[0];

  const handleCardClick = (product) => {
    setSelectedId(product.id);
    navigate(product.path);
  };

  return (
    <div style={{ marginTop: '80px' }}>
      <section
        style={{
          padding: '5.5rem 0',
          background:
            'radial-gradient(circle at top left, rgba(56,189,248,.20), transparent 32%), radial-gradient(circle at bottom right, rgba(99,102,241,.18), transparent 34%), linear-gradient(135deg, #020617 0%, #0f172a 52%, #111827 100%)',
          overflow: 'hidden',
        }}
      >
        <div className="container">
          <FadeIn>
            <div style={{ maxWidth: 860, margin: '0 auto', textAlign: 'center' }}>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '.5rem',
                  padding: '.5rem 1rem',
                  borderRadius: '999px',
                  background: 'rgba(56,189,248,.12)',
                  border: '1px solid rgba(56,189,248,.32)',
                  color: '#7dd3fc',
                  fontWeight: 700,
                  marginBottom: '1.5rem',
                }}
              >
                <i className="fas fa-layer-group" />
                Catálogo de soluções
              </div>

              <h1
                style={{
                  fontSize: 'clamp(2.4rem, 5vw, 4.2rem)',
                  fontWeight: 900,
                  color: '#fff',
                  lineHeight: 1.05,
                  marginBottom: '1.5rem',
                  letterSpacing: '-1.5px',
                }}
              >
                Escolha o sistema ideal para{' '}
                <span
                  style={{
                    background:
                      'linear-gradient(135deg, #38bdf8, #22d3ee, #6366f1)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  sua operação
                </span>
              </h1>

              <p
                style={{
                  color: '#cbd5e1',
                  fontSize: '1.16rem',
                  lineHeight: 1.7,
                  maxWidth: 720,
                  margin: '0 auto',
                }}
              >
                Veja nossas plataformas, filtre por segmento e clique em uma
                solução para conhecer os detalhes.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      <section style={{ padding: '4.5rem 0 5rem', background: 'var(--bg-primary)' }}>
        <div className="container">
          <div
            className="solutions-page-title"
            style={{
              textAlign: 'center',
              marginBottom: '3rem',
              maxWidth: '900px',
              marginInline: 'auto',
            }}
          >
            <h2
              style={{
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                color: 'var(--dark)',
                marginBottom: '.75rem',
                lineHeight: 1.1,
              }}
            >
              Soluções disponíveis
            </h2>

            <p
              style={{
                color: 'var(--gray)',
                fontSize: '1.08rem',
                lineHeight: 1.7,
                margin: 0,
              }}
            >
              Escolha a solução ideal para sua operação. Atualmente temos{' '}
              {filteredProducts.length} produto(s) disponível(is).
            </p>
          </div>

          <div
            className="solutions-layout"
            style={{
              display: 'grid',
              gridTemplateColumns: '280px 1fr',
              gap: '2rem',
              alignItems: 'start',
            }}
          >
            <aside
              className="solutions-sidebar"
              style={{
                position: 'sticky',
                top: '105px',
                display: 'grid',
                gap: '1.5rem',
              }}
            >
              <div
                style={{
                  padding: '1.5rem',
                  borderRadius: '24px',
                  background: 'var(--bg-tertiary)',
                  border: '1px solid rgba(148,163,184,.18)',
                }}
              >
                <strong
                  style={{
                    display: 'block',
                    marginBottom: '1rem',
                    color: 'var(--dark)',
                  }}
                >
                  Buscar solução
                </strong>

                <div style={{ position: 'relative' }}>
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Ex.: fazenda, obras, WhatsApp..."
                    style={{
                      width: '100%',
                      padding: '.9rem 2.7rem .9rem 1rem',
                      borderRadius: '14px',
                      border: '1px solid rgba(148,163,184,.25)',
                      background: 'var(--bg-primary)',
                      color: 'var(--dark)',
                      outline: 'none',
                    }}
                  />
                  <i
                    className="fas fa-search"
                    style={{
                      position: 'absolute',
                      right: '1rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'var(--gray)',
                    }}
                  />
                </div>
              </div>

              <div
                style={{
                  padding: '1.5rem',
                  borderRadius: '24px',
                  background: 'var(--bg-tertiary)',
                  border: '1px solid rgba(148,163,184,.18)',
                }}
              >
                <strong
                  style={{
                    display: 'block',
                    marginBottom: '1rem',
                    color: 'var(--dark)',
                  }}
                >
                  Categorias
                </strong>

                <div style={{ display: 'grid', gap: '.65rem' }}>
                  {categories.map((item) => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => setCategory(item)}
                      style={{
                        padding: '.75rem .9rem',
                        borderRadius: '14px',
                        border:
                          category === item
                            ? '1px solid var(--primary)'
                            : '1px solid rgba(148,163,184,.16)',
                        background:
                          category === item
                            ? 'rgba(56,189,248,.12)'
                            : 'var(--bg-primary)',
                        color: category === item ? 'var(--primary)' : 'var(--dark)',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontWeight: 700,
                      }}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </div>
            </aside>

            <main>
              <div
                className="solutions-cards-grid"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                  gap: '1.5rem',
                }}
              >
                {filteredProducts.map((product) => (
                  <motion.button
                    key={product.id}
                    type="button"
                    onClick={() => handleCardClick(product)}
                    whileHover={{ y: -6 }}
                    style={{
                      border:
                        selectedId === product.id
                          ? `2px solid ${product.color}`
                          : '1px solid rgba(148,163,184,.18)',
                      borderRadius: '26px',
                      padding: '1rem',
                      textAlign: 'left',
                      background: 'var(--bg-tertiary)',
                      cursor: 'pointer',
                      boxShadow:
                        selectedId === product.id
                          ? `0 24px 60px ${product.color}22`
                          : '0 16px 40px rgba(15,23,42,.07)',
                    }}
                  >
                    <div
                      style={{
                        height: 160,
                        borderRadius: '20px',
                        overflow: 'hidden',
                        background: `linear-gradient(135deg, ${product.color}33, rgba(99,102,241,.12))`,
                        display: 'grid',
                        placeItems: 'center',
                        marginBottom: '1rem',
                      }}
                    >
                      {product.image ? (
                        <img
                          src={product.image}
                          alt={product.title}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      ) : (
                        <i
                          className={product.icon}
                          style={{ fontSize: '4rem', color: product.color }}
                        />
                      )}
                    </div>

                    <small
                      style={{
                        color: product.color,
                        fontWeight: 800,
                        textTransform: 'uppercase',
                        letterSpacing: '.08em',
                      }}
                    >
                      {product.category}
                    </small>

                    <h3
                      style={{
                        color: 'var(--dark)',
                        margin: '.5rem 0 .6rem',
                        fontSize: '1.2rem',
                      }}
                    >
                      {product.title}
                    </h3>

                    <p
                      style={{
                        color: 'var(--gray)',
                        lineHeight: 1.55,
                        margin: 0,
                        fontSize: '.94rem',
                      }}
                    >
                      {product.short}
                    </p>
                  </motion.button>
                ))}
              </div>

              {filteredProducts.length === 0 && (
                <div
                  style={{
                    padding: '3rem',
                    borderRadius: '24px',
                    background: 'var(--bg-tertiary)',
                    border: '1px solid rgba(148,163,184,.18)',
                    textAlign: 'center',
                    color: 'var(--gray)',
                  }}
                >
                  Nenhuma solução encontrada para os filtros selecionados.
                </div>
              )}

              <motion.div
                key={selectedProduct.id}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                style={{
                  marginTop: '3rem',
                  borderRadius: '32px',
                  overflow: 'hidden',
                  background: 'var(--bg-tertiary)',
                  border: '1px solid rgba(148,163,184,.18)',
                  boxShadow: '0 24px 70px rgba(15,23,42,.10)',
                }}
              >
                <div
                  className="solution-detail"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(280px, .9fr) 1.1fr',
                  }}
                >
                  <div
                    style={{
                      minHeight: 360,
                      background: `linear-gradient(135deg, ${selectedProduct.color}, #0f172a)`,
                      display: 'grid',
                      placeItems: 'center',
                      overflow: 'hidden',
                    }}
                  >
                    {selectedProduct.image ? (
                      <img
                        src={selectedProduct.image}
                        alt={selectedProduct.title}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    ) : (
                      <i
                        className={selectedProduct.icon}
                        style={{
                          fontSize: '6rem',
                          color: '#fff',
                          filter: 'drop-shadow(0 18px 28px rgba(0,0,0,.28))',
                        }}
                      />
                    )}
                  </div>

                  <div style={{ padding: '2rem' }}>
                    <div
                      style={{
                        width: 58,
                        height: 58,
                        borderRadius: 18,
                        background: `${selectedProduct.color}22`,
                        color: selectedProduct.color,
                        display: 'grid',
                        placeItems: 'center',
                        fontSize: '1.5rem',
                        marginBottom: '1rem',
                      }}
                    >
                      <i className={selectedProduct.icon} />
                    </div>

                    <small
                      style={{
                        color: selectedProduct.color,
                        fontWeight: 800,
                        textTransform: 'uppercase',
                        letterSpacing: '.08em',
                      }}
                    >
                      {selectedProduct.category}
                    </small>

                    <h2
                      style={{
                        color: 'var(--dark)',
                        fontSize: 'clamp(2rem, 4vw, 3rem)',
                        margin: '.5rem 0 1rem',
                      }}
                    >
                      {selectedProduct.title}
                    </h2>

                    <p
                      style={{
                        color: 'var(--gray)',
                        lineHeight: 1.75,
                        fontSize: '1.05rem',
                        marginBottom: '1.5rem',
                      }}
                    >
                      {selectedProduct.description}
                    </p>

                    <strong
                      style={{
                        display: 'block',
                        color: 'var(--dark)',
                        marginBottom: '1rem',
                      }}
                    >
                      Principais recursos:
                    </strong>

                    <div style={{ display: 'grid', gap: '.75rem', marginBottom: '2rem' }}>
                      {selectedProduct.features.map((feature) => (
                        <div
                          key={feature}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '.75rem',
                            color: 'var(--dark)',
                          }}
                        >
                          <i
                            className="fas fa-check-circle"
                            style={{ color: selectedProduct.color }}
                          />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        gap: '1rem',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                      }}
                    >
                      <Button to={selectedProduct.path}>Conhecer Produto</Button>

                      <Button variant="secondary" to={selectedProduct.plansPath}>
                        Ver Planos
                      </Button>

                      <strong style={{ color: selectedProduct.color }}>
                        {selectedProduct.price}
                      </strong>
                    </div>
                  </div>
                </div>
              </motion.div>
            </main>
          </div>
        </div>

        <style>{`
          @media (max-width: 900px) {
            .solutions-layout {
              grid-template-columns: 1fr !important;
            }

            .solutions-sidebar {
              position: static !important;
            }

            .solution-detail {
              grid-template-columns: 1fr !important;
            }
          }
        `}</style>
      </section>
    </div>
  );
};

export default Solucoes;